/* Trailblazer — quantity tier selector.
   Sets the product form's quantity input when a pack option is chosen. */
if (!customElements.get('tb-quantity-tiers')) {
  customElements.define(
    'tb-quantity-tiers',
    class TbQuantityTiers extends HTMLElement {
      connectedCallback() {
        this.inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
        this.inputs.forEach((input) => {
          input.addEventListener('change', () => this.onChange(input));
        });
        const checked = this.inputs.find((i) => i.checked);
        if (checked) this.setQuantity(checked.value);
      }

      onChange(input) {
        this.querySelectorAll('.tb-tier').forEach((el) => el.classList.remove('tb-tier--active'));
        const label = input.closest('.tb-tier');
        if (label) label.classList.add('tb-tier--active');
        this.setQuantity(input.value);
      }

      setQuantity(value) {
        const id = this.dataset.quantityInput;
        const field =
          (id && document.getElementById(id)) ||
          (this.closest('.product') || document).querySelector('input[name="quantity"]');
        if (!field) return;
        field.value = value;
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );
}

/* Trailblazer — cart add-ons (upsell rows) */
if (!customElements.get('tb-cart-upsell')) {
  customElements.define(
    'tb-cart-upsell',
    class TbCartUpsell extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-tb-add]');
          if (btn) this.add(btn);
        });
      }

      async add(btn) {
        const id = btn.dataset.tbAdd;
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify({
              items: [{ id: Number(id), quantity: 1 }],
              sections: 'cart-drawer,cart-icon-bubble,main-cart-items,main-cart-footer',
              sections_url: window.location.pathname,
            }),
          });
          const data = await res.json();
          const drawer = document.querySelector('cart-drawer');
          if (drawer && data.sections && typeof drawer.renderContents === 'function') {
            drawer.renderContents(data);
          } else {
            window.location.reload();
          }
        } catch (err) {
          btn.disabled = false;
          btn.textContent = original;
        }
      }
    }
  );
}

/* Trailblazer — sticky add-to-bag on mobile */
if (!customElements.get('tb-sticky-atc')) {
  customElements.define(
    'tb-sticky-atc',
    class TbStickyAtc extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('.tb-sticky');
        this.target = document.querySelector('.product-form__submit');
        if (!this.bar || !this.target) return;

        this.querySelector('[data-tb-sticky-add]')?.addEventListener('click', () => {
          this.target.click();
        });

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              const show = !entry.isIntersecting;
              this.bar.classList.toggle('tb-sticky--in', show);
              // Tells the page to reserve the bar's height, so nothing ends up
              // permanently trapped underneath it.
              document.body.classList.toggle('tb-sticky-on', show);
            },
            { rootMargin: '0px 0px -60px 0px' }
          );
          this.observer.observe(this.target);
        } else {
          this.bar.classList.add('tb-sticky--in');
        }

        // The price was printed once by Liquid and then never moved, so the bar
        // said $26.99 while the page said $68.82. Rather than recompute the
        // maths a second time and risk the two disagreeing, it mirrors whatever
        // the selected pack card is showing — one source, always in step.
        this.priceEl = this.querySelector('.tb-sticky__price');
        this.syncPrice = () => {
          if (!this.priceEl) return;
          const chosen = document.querySelector('.tb-tier--active .tb-tier__price')
            || document.querySelector('.price__container .price-item--sale')
            || document.querySelector('.price__container .price-item--regular');
          const text = chosen ? chosen.textContent.trim() : '';
          if (text) this.priceEl.textContent = text;
        };
        this.onChange = () => requestAnimationFrame(this.syncPrice);
        document.addEventListener('change', this.onChange);
        document.addEventListener('click', this.onChange);
        this.syncPrice();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.body.classList.remove('tb-sticky-on');
        document.removeEventListener('change', this.onChange);
        document.removeEventListener('click', this.onChange);
      }
    }
  );
}

/* Trailblazer — delivery estimate.
   Computed in the shopper's browser from THEIR clock every time the page loads,
   so it re-dates itself daily and can never show a stale window. Handling runs
   Mon–Fri; USPS Ground Advantage delivers Mon–Sat, so transit skips Sundays and
   US federal holidays only. */
if (!customElements.get('tb-delivery')) {
  const TB_HOLIDAYS = new Set([
    // US federal holidays on which neither the warehouse nor USPS moves.
    '2026-10-12', '2026-11-11', '2026-11-26', '2026-12-25',
    '2027-01-01', '2027-01-18', '2027-02-15', '2027-05-31', '2027-06-18',
    '2027-07-05', '2027-09-06', '2027-10-11', '2027-11-11', '2027-11-25', '2027-12-24',
  ]);

  const tbKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  customElements.define(
    'tb-delivery',
    class TbDelivery extends HTMLElement {
      connectedCallback() {
        this.render();
        // A tab left open overnight would otherwise keep yesterday's dates.
        this.timer = setInterval(() => this.render(), 30 * 60 * 1000);
        document.addEventListener('visibilitychange', this.onShow = () => {
          if (!document.hidden) this.render();
        });
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        document.removeEventListener('visibilitychange', this.onShow);
      }

      closed(date, saturdayCounts) {
        const day = date.getDay();
        if (day === 0) return true;                    // Sunday: nobody moves
        if (day === 6 && !saturdayCounts) return true; // Saturday: carrier only
        return TB_HOLIDAYS.has(tbKey(date));
      }

      advance(from, days, saturdayCounts) {
        const date = new Date(from.getTime());
        let left = days;
        while (left > 0) {
          date.setDate(date.getDate() + 1);
          if (!this.closed(date, saturdayCounts)) left -= 1;
        }
        return date;
      }

      render() {
        const handling = Number(this.dataset.handlingDays);
        const fast = Number(this.dataset.minTransit);
        const slow = Number(this.dataset.maxTransit);
        if (!Number.isFinite(handling) || !Number.isFinite(fast) || !Number.isFinite(slow)) return;

        const now = new Date();
        const cutoff = Number(this.dataset.cutoffHour);
        // Past the cutoff, today no longer counts as a packing day.
        let base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (Number.isFinite(cutoff) && now.getHours() >= cutoff) {
          base = this.advance(base, 1, false);
        }
        const ships = this.advance(base, handling, false);
        const earliest = this.advance(ships, fast, true);
        const latest = this.advance(ships, slow, true);

        const short = { weekday: 'short', month: 'short', day: 'numeric' };
        const noDay = { month: 'short', day: 'numeric' };
        const one = (d, opts) => d.toLocaleDateString(undefined, opts);
        const sameMonth = earliest.getMonth() === latest.getMonth();
        const window = `${one(earliest, short)} – ${one(latest, sameMonth ? { day: 'numeric' } : noDay)}`;

        const out = this.querySelector('[data-tb-delivery-text]') || this;
        out.innerHTML =
          `<span class="tb-deliv__lead">${this.dataset.label || 'Get it'}</span> `
          + `<strong>${window}</strong>`;
        this.setAttribute('data-ready', 'true');
      }
    }
  );
}

/* Trailblazer — small helpers the builder leans on. */

// Browser storage throws outright in some privacy modes, so every touch is
// wrapped and every failure is silent. Nothing here is load-bearing.
const TB_STORE = {
  read(key) {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  },
  write(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      /* full, blocked, or private mode — carry on without it */
    }
  },
  drop(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (err) {
      /* nothing to do */
    }
  },
};

// One place to shout from, so the builder does not care who is listening.
// Anything present gets told; anything absent is skipped without error.
function tbTrack(name, payload) {
  const detail = Object.assign({ event: `tb_${name}` }, payload || {});
  try {
    document.dispatchEvent(new CustomEvent(`tb:${name}`, { detail, bubbles: true }));
  } catch (err) {
    /* ancient browser */
  }
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(detail);
  } catch (err) {
    /* no GTM */
  }
  try {
    if (typeof window.gtag === 'function') window.gtag('event', `tb_${name}`, payload || {});
  } catch (err) {
    /* no GA4 */
  }
}

/* Trailblazer — build your pack */
var TB_BUILD = '2026-09-19a';

// Stamped onto every cart line the builder adds, and onto the cart itself.
// Two jobs: a second checkout knows which lines were its own and replaces
// them, and the finished order carries a mark that Flow can tag on.
var TB_PACK_MARK = 'Build Your Pack';

// This file is pulled in by several sections AND, so the cart and every other
// page get it too, by the theme layout. A page carrying both would run it
// twice and double every listener. The custom elements were already guarded;
// this guards the one-time page wiring below.
var TB_BOOTED = window.__TB_BOOTED === true;
window.__TB_BOOTED = true;

if (!customElements.get('tb-bundle-builder')) {
  customElements.define(
    'tb-bundle-builder',
    class TbBundleBuilder extends HTMLElement {
      connectedCallback() {
        this.currency = this.dataset.currency || 'USD';
        this.tiers = this.parse(this.dataset.tiers, []).sort((a, b) => a.at - b.at);
        this.max = Number(this.dataset.max) || 1;
        this.mode = this.dataset.mode === 'value' ? 'value' : 'packs';
        this.eligible = new Set(this.parse(this.dataset.eligible, []).map(Number));
        // No list at all (or a collection that turned out empty) means every
        // product counts. It must never mean that nothing does.
        this.eligibleAll = this.eligible.size === 0;
        this.freeCents = Number(this.dataset.freeCents) || 0;
        this.inBag = 0;
        this.inBagValue = 0;
        this.lastPct = 0;
        this.lastFree = false;

        // lines: Map keyed by variant id -> { id, qty, price, title, detail, productId }
        this.lines = new Map();
        // Two levels, deliberately. The master switch says "I want to
        // subscribe" and opens the question; each line then answers it for
        // itself, with its own quantity. Off at either level means no
        // subscription line for that item.
        this.subOff = new Set();   // lines switched off inside an on pack
        this.repeatQty = new Map(); // how many of that line keep coming
        this.tiles = Array.from(this.querySelectorAll('[data-tb-tile]'));

        // plan id -> "Every 3 months". Each product has its own ids for the
        // same wording, so the cadence is what the two sides agree on.
        const cadRaw = this.querySelector('[data-tb-cadences]');
        this.cadences = cadRaw ? this.parse(cadRaw.textContent, {}) : {};
        const nameRaw = this.querySelector('[data-tb-cadence-names]');
        this.everyNames = nameRaw ? this.parse(nameRaw.textContent, []) : [];
        if (!this.everyNames.length) {
          // The list is the reliable source because it is ordered. If it is
          // missing, the map's values still name every cadence on offer — worse
          // order, but far better than the page deciding nothing can repeat.
          const seen = [];
          Object.keys(this.cadences).forEach((id) => {
            const nm = this.cadences[id];
            if (nm && seen.indexOf(nm) === -1) seen.push(nm);
          });
          this.everyNames = seen;
        }
        this.defaultEvery = this.everyNames[0] || '';
        this.repeatEvery = new Map();

        this.tiles.forEach((tile) => {
          const raw = tile.querySelector('[data-tb-variants]');
          tile.variants = raw ? this.parse(raw.textContent, []) : [];
          const addButton = tile.querySelector('[data-tb-add]');
          if (addButton) {
            addButton.dataset.label = addButton.textContent.trim();
            addButton.addEventListener('click', () => this.add(tile));
            // Once something is in the pack the button becomes a quantity
            // control. Pressing "Add to pack" a second time works, but nobody
            // should have to guess that — the stepper says it out loud.
            const stepper = document.createElement('div');
            stepper.className = 'tb-bundle__qty';
            stepper.hidden = true;
            stepper.innerHTML =
              '<button type="button" data-tb-tile-step="-1" aria-label="Remove one">&minus;</button>'
              + '<b><span data-tb-tile-qty>0</span> <i>in pack</i></b>'
              + '<button type="button" data-tb-tile-step="1" aria-label="Add one">+</button>';
            addButton.insertAdjacentElement('afterend', stepper);
            tile.stepper = stepper;
            stepper.addEventListener('click', (event) => {
              const button = event.target.closest('[data-tb-tile-step]');
              if (!button) return;
              const variant = this.currentVariant(tile);
              if (variant) this.step(variant.id, Number(button.dataset.tbTileStep));
            });
          }
          tile.querySelectorAll('[data-tb-option]').forEach((sel) => {
            sel.addEventListener('change', () => this.onOptionChange(tile));
          });
        });

        this.buildStops();

        this.list = this.querySelector('[data-tb-list]');
        this.list?.addEventListener('click', (event) => {
          const sub = event.target.closest('[data-tb-line-sub]');
          if (sub) {
            const key = String(sub.dataset.tbLineSub);
            if (!this.repeating()) {
              // Coming in through one item: that item, and nothing else. The
              // master switch follows, because there IS a subscription now —
              // but it does not drag the rest of the pack in with it.
              this.subOff.clear();
              this.lines.forEach((other) => {
                if (String(other.id) !== key) this.subOff.add(String(other.id));
              });
              const yes = this.repeatBox
                && this.repeatBox.querySelector('[data-tb-repeat-mode][value="repeat"]');
              if (yes) yes.checked = true;
              tbTrack('resupply_mode', { on: true, from: 'line' });
            } else if (this.subOff.has(key)) {
              this.subOff.delete(key);
            } else {
              this.subOff.add(key);
              // Switching off the last one means nothing is repeating, so the
              // master should say so rather than claiming a subscription that
              // has no items in it.
              let any = false;
              this.lines.forEach((other) => {
                if (this.hasAnyPlan(other) && !this.subOff.has(String(other.id))) any = true;
              });
              if (!any) {
                const no = this.repeatBox
                  && this.repeatBox.querySelector('[data-tb-repeat-mode][value="once"]');
                if (no) no.checked = true;
              }
            }
            tbTrack('resupply_line', { on: !this.subOff.has(key) });
            this.render();
            return;
          }
          const rep = event.target.closest('[data-tb-rep-step]');
          if (rep) {
            const key = String(rep.dataset.variant);
            const line = this.lines.get(key);
            if (!line) return;
            let next = this.repeatQtyFor(line) + Number(rep.dataset.tbRepStep);
            if (next < 1) next = 1;
            if (next > 10) next = 10;
            this.repeatQty.set(key, next);
            // What repeats ships today too, so raising the subscription raises
            // today's quantity rather than being capped by it.
            if (next > line.qty) this.step(key, next - line.qty);
            else this.render();
            return;
          }
          const button = event.target.closest('[data-tb-step]');
          if (!button) return;
          this.step(button.dataset.variant, Number(button.dataset.tbStep));
        });
        this.list?.addEventListener('change', (event) => {
          const sel = event.target.closest('[data-tb-line-every]');
          if (!sel) return;
          this.repeatEvery.set(String(sel.dataset.tbLineEvery), sel.value);
          this.render();
        });

        this.addButton = this.querySelector('[data-tb-bundle-add]');
        this.addButton?.addEventListener('click', () => this.addAll());
        this.wireRepeat();
        this.makeStickyBar();
        this.wireQuickView();

        // Emptying the pack used to leave the last "$X more for..." line on
        // screen, which reads as a promise about a bag that no longer exists.
        const startMsg = this.querySelector('[data-tb-meter-msg]');
        this.startNote = startMsg ? startMsg.innerHTML : '';

        this.packKey = 'tb-pack-v1';
        this.sizeKey = 'tb-size-v1';
        this.restoreSizes();
        this.restorePack();
        this.seedFromLink();

        this.makeDebug();
        this.render();
        if (this.dataset.countCart === 'true') this.readBag();
      }

      // On a phone the pack summary sits below every tile, so someone who has
      // chosen three things has to scroll back past the whole grid to buy. This
      // rides along at the bottom, and gets out of the way the moment the real
      // summary button is on screen.
      makeStickyBar() {
        if (!this.addButton) return;
        const bar = document.createElement('div');
        bar.className = 'tb-dock';
        bar.hidden = true;
        // "Subscribe to socks" means going to the socks and pressing the thing
        // that says subscribe. Watching somebody build a whole pack, get told
        // to subscribe to one item, ignore every switch and the bar, scroll
        // back up and press this — which was a label — settled what it should
        // be. A black filled box in this theme is a button everywhere else.
        //
        // It puts the item in the pack if it is not there, subscribes that one
        // line and nothing else, and takes them to the pack to see it. The
        // quantity and frequency still live there; this is the way in.
        this.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-tb-subchip]');
          if (!chip) return;
          event.preventDefault();
          event.stopPropagation();
          const tile = chip.closest('[data-tb-tile]');
          if (!tile) return;
          const variant = this.currentVariant(tile);
          if (!variant || !variant.available) return;
          const key = String(variant.id);
          if (!this.lines.has(key)) this.add(tile);

          if (!this.repeating()) {
            // Same narrow door as a line's own switch: this item, not the pack.
            this.subOff.clear();
            this.lines.forEach((other) => {
              if (String(other.id) !== key) this.subOff.add(String(other.id));
            });
            const yes = this.repeatBox
              && this.repeatBox.querySelector('[data-tb-repeat-mode][value="repeat"]');
            if (yes) yes.checked = true;
          } else {
            this.subOff.delete(key);
          }
          tbTrack('resupply_mode', { on: true, from: 'chip' });
          this.render();
          this.goToPack();
        });

        // A strip above the total, carried the whole way down the grid. The
        // pack summary is off screen for most of the build, which is exactly
        // the stretch where subscribing has to stay visible. It does not
        // subscribe anybody — it takes them to the choice, which is one place
        // and stays one place.
        bar.innerHTML =
          '<div class="tb-dock__row">'
          + '<div class="tb-dock__facts">'
          + '<b data-tb-dock-total></b>'
          + '<span data-tb-dock-note></span>'
          + '</div>'
          + '</div>'
          + '<div class="tb-dock__acts">'
          + '<button type="button" class="tb-dock__sub-btn" data-tb-dock-sub hidden>'
          + '<span data-tb-dock-sub-text></span>'
          + '</button>'
          + '<button type="button" class="tb-dock__go"></button>'
          + '</div>';
        // Its OWN label, and its own job. Copying the pack button's text is why
        // renaming that to "Check out" renamed this too — and it does not check
        // anybody out, it takes them to the pack.
        this.dockGo = bar.querySelector('.tb-dock__go');
        this.dockGo.textContent = this.dataset.dockLabel || 'View my cart';
        this.dockGo.addEventListener('click', () => this.goToPack());
        // Turns subscribing on and takes them to the pack in one press. The
        // per-item switches are the point of the feature and they live down
        // there, so a version of this that flipped a switch and left them at
        // the top of the grid would hide the thing it just turned on.
        bar.querySelector('[data-tb-dock-sub]').addEventListener('click', () => {
          this.setRepeat(true);
          this.goToPack();
        });


        document.body.appendChild(bar);
        this.dock = bar;
        this.summaryEl = this.querySelector('.tb-bundle__summary');

        if ('IntersectionObserver' in window) {
          // Fine steps, because the test is "how much of the pack can I see",
          // not "can I see any of it" — and a coarse threshold list would let
          // the bar flip somewhere other than where the rule says.
          const steps = [];
          for (let i = 0; i <= 50; i += 1) steps.push(i / 50);

          this.dockWatch = new IntersectionObserver(
            ([entry]) => {
              // The bar goes when the pack has taken over the screen — half of
              // the usable height, or half the pack itself when the pack is the
              // shorter of the two. A sliver of it appearing at the bottom of
              // the grid is not arriving at it, and that is when the bar is
              // still doing its job.
              const shown = entry.intersectionRect.height;
              const box = entry.boundingClientRect.height || 1;
              const reach = Math.min(window.innerHeight - this.dockHeight(), box);
              this.summaryOnScreen = reach > 0 && shown / reach >= 0.5;
              this.paintDock();
            },
            // Watching the pack itself, not the button at its foot: the bar
            // exists to get you to the pack, so the moment the pack is on
            // screen it has nothing left to offer. Scroll back above it and
            // it returns.
            //
            // The bottom margin is the bar's own height — otherwise the pack
            // peeking out from underneath it counts as visible.
            { rootMargin: `0px 0px -${this.dockHeight()}px 0px`, threshold: steps }
          );
          this.dockWatch.observe(this.summaryEl || this.addButton);
        }
      }

      dockHeight() {
        return (this.dock && this.dock.offsetHeight) || 110;
      }

      paintDock() {
        if (!this.dock) return;
        const show = this.lines.size > 0 && !this.summaryOnScreen;
        this.dock.hidden = !show;
        document.body.classList.toggle('tb-dock-on', show);

        const strip = this.dock.querySelector('[data-tb-dock-sub]');
        if (!strip) return;
        strip.hidden = !this.repeatBox;
        const on = this.repeating();
        strip.classList.toggle('is-on', on);
        const text = strip.querySelector('[data-tb-dock-sub-text]');
        if (text) {
          const off = this.subOffPct || 0;
          text.textContent = on
            ? 'Set up subscription'
            : 'Subscribe to get ' + (off || 10) + '% off';
        }
      }

      // Both buttons in the bar end up at the pack, because that is where the
      // decisions are and where the order is placed.
      goToPack() {
        // The TOP of the pack, not the button at its foot. Somebody asking to
        // see their cart wants to read it from the start — the choice, then
        // the items — not to be dropped at the bottom of it.
        const anchor = this.summaryEl || this.addButton || this.repeatBox;
        if (!anchor) return;

        // The meter is sticky, so aligning the pack with the top of the window
        // would slide it underneath. Measured rather than assumed, because
        // that bar's height changes with the message in it.
        const meter = this.querySelector('[data-tb-meter]');
        let offset = 0;
        if (meter && getComputedStyle(meter).position === 'sticky') {
          offset = meter.offsetHeight;
        }
        const top = window.scrollY + anchor.getBoundingClientRect().top - offset - 12;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

        const flash = this.repeatBox || anchor;
        flash.classList.add('tb-repeat--flash');
        clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(() => flash.classList.remove('tb-repeat--flash'), 1400);

        // Belt and braces: a smooth scroll that gets interrupted, or a browser
        // that fires the observer late, would otherwise strand the bar on
        // screen. Scrolling back up brings it straight back.
        clearTimeout(this.dockHideTimer);
        this.dockHideTimer = setTimeout(() => {
          if (!this.dock || this.dock.hidden) return;
          const box = anchor.getBoundingClientRect();
          const room = window.innerHeight - this.dockHeight();
          const shown = Math.min(box.bottom, room) - Math.max(box.top, 0);
          const reach = Math.min(room, box.height || 1);
          if (reach > 0 && shown / reach >= 0.5) {
            this.summaryOnScreen = true;
            this.paintDock();
          }
        }, 700);
      }

      clearSubPrefs() {
        this.subOff.clear();
        this.repeatQty.clear();
        this.repeatEvery.clear();
        const no = this.repeatBox
          && this.repeatBox.querySelector('[data-tb-repeat-mode][value="once"]');
        if (no) no.checked = true;
      }

      setRepeat(on) {
        const want = on ? 'repeat' : 'once';
        const el = this.repeatBox
          && this.repeatBox.querySelector(`[data-tb-repeat-mode][value="${want}"]`);
        if (!el) return;

        // Turning it on is a fresh start, whether or not it was already on.
        // Pressing Subscribe and watching nothing happen because the state was
        // technically already "subscribed" is the worst kind of dead button.
        if (on) this.subOff.clear();
        const changed = !el.checked;
        el.checked = true;
        if (!changed && !on) return;
        tbTrack('resupply_mode', { on: on, from: 'dock' });
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onQuickKey);
        document.body.classList.remove('tb-quick-on');
        this.quick?.remove();
        this.dockWatch?.disconnect();
        this.dock?.remove();
        document.body.classList.remove('tb-dock-on');
      }

      // ---- quick view: get informed without leaving the page -----------
      // A cold visitor should not have to choose between buying blind and
      // navigating away. The drawer body is fetched from its own section the
      // first time it is asked for, then kept.
      wireQuickView() {
        this.quick = this.querySelector('[data-tb-quick]');
        if (!this.quick) return;
        // position:fixed resolves against the nearest ancestor carrying a
        // transform, not the viewport. Dawn's scroll-in animation puts a
        // transform on the section while it slides into view, which trapped the
        // drawer inside the section's box and cropped it. Hanging it off <body>
        // means no ancestor can ever do that again.
        document.body.appendChild(this.quick);
        this.quickSlot = this.quick.querySelector('[data-tb-quick-slot]');
        this.quickFoot = this.quick.querySelector('[data-tb-quick-foot]');
        this.quickAdd = this.quick.querySelector('[data-tb-quick-add]');
        this.quickCache = new Map();

        this.addEventListener('click', (event) => {
          const opener = event.target.closest('[data-tb-open]');
          if (!opener) return;
          const tile = opener.closest('[data-tb-tile]');
          if (tile) this.openQuick(tile);
        });

        this.quick.querySelectorAll('[data-tb-quick-close]').forEach((el) => {
          el.addEventListener('click', () => this.closeQuick());
        });
        // Escape, the scrim and a swipe down all close it. Trapping someone in a
        // panel they cannot dismiss reads as a trick and costs more than it saves.
        this.onQuickKey = (event) => {
          if (event.key === 'Escape' && !this.quick.hidden) this.closeQuick();
        };
        document.addEventListener('keydown', this.onQuickKey);

        // The panel moves with the thumb so it is obvious a hard pull dismisses it
        // and a small one does not. The old version closed on a 90px drag with no
        // feedback, which meant a stray scroll threw the whole drawer away.
        const panel = this.quick.querySelector('.tb-quick__panel');
        this.quickPanel = panel;
        const scrim = this.quick.querySelector('.tb-quick__scrim');
        let startY = null;
        let startT = 0;
        let dy = 0;

        const setDrag = (px) => {
          panel.style.transform = px ? `translateY(${px}px)` : '';
          if (scrim) scrim.style.opacity = px ? String(Math.max(0.25, 1 - px / 420)) : '';
        };

        panel?.addEventListener('touchstart', (event) => {
          if (event.touches.length !== 1) return;
          // Only when the content is already scrolled to the top, so a normal
          // read never turns into a dismiss.
          if (this.quickSlot && this.quickSlot.scrollTop > 0) { startY = null; return; }
          startY = event.touches[0].clientY;
          startT = Date.now();
          dy = 0;
          panel.style.transition = 'none';
        }, { passive: true });

        panel?.addEventListener('touchmove', (event) => {
          if (startY === null) return;
          const raw = event.touches[0].clientY - startY;
          if (raw <= 0) { dy = 0; setDrag(0); return; }
          // One to one with the thumb. Damping made the panel lag the finger,
          // which reads as sluggish and hides how far you actually have to pull.
          dy = raw;
          setDrag(dy);
        }, { passive: true });

        const settle = () => {
          if (startY === null) return;
          // About a quarter of the panel's own height, so the distance scales
          // with the sheet instead of being a number that suits one phone.
          const needed = Math.max(120, panel.getBoundingClientRect().height * 0.24);
          const flick = dy > 70 && Date.now() - startT < 240;
          panel.style.transition = 'transform .2s ease-out';
          if (dy > needed || flick) {
            setDrag(panel.getBoundingClientRect().height);
            setTimeout(() => this.closeQuick(), 160);
          } else {
            setDrag(0);
          }
          startY = null;
        };
        panel?.addEventListener('touchend', settle);
        panel?.addEventListener('touchcancel', settle);

        this.quickAdd?.addEventListener('click', () => this.addFromQuick());

        // Opening should feel instant. Warm the body on the first sign of
        // intent — a finger landing on the tile, or a cursor arriving — which
        // buys 100-300ms, and quietly fetch the rest once the page is idle.
        const warm = (event) => {
          const tile = event.target.closest?.('[data-tb-tile]');
          if (tile) this.warmQuick(tile.dataset.handle);
        };
        this.addEventListener('pointerenter', warm, true);
        this.addEventListener('touchstart', warm, { passive: true, capture: true });

        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
        idle(() => {
          this.tiles.forEach((tile, i) => {
            setTimeout(() => this.warmQuick(tile.dataset.handle), i * 250);
          });
        });
      }

      warmQuick(handle) {
        if (!handle || this.quickCache.has(handle)) return;
        // Mark it first so two events cannot start the same request twice.
        this.quickCache.set(handle, null);
        fetch(`${window.Shopify.routes.root}products/${handle}?section_id=tb-quick`)
          .then((res) => res.text())
          .then((html) => { this.quickCache.set(handle, html); })
          .catch(() => { this.quickCache.delete(handle); });
      }

      async openQuick(tile) {
        const handle = tile.dataset.handle;
        if (!handle) return;
        this.quickTile = tile;
        this.quickReturn = tile.querySelector('[data-tb-open]');
        this.quick.hidden = false;
        document.body.classList.add('tb-quick-on');
        this.quickFoot.hidden = true;
        tbTrack('quick_view_opened', { product_title: tile.dataset.title || '', handle });

        let html = this.quickCache.get(handle);
        if (html == null) {
          this.quickSlot.innerHTML = '<p class="tb-quick__loading">Loading&hellip;</p>';
          try {
            const res = await fetch(`${window.Shopify.routes.root}products/${handle}?section_id=tb-quick`);
            html = await res.text();
            this.quickCache.set(handle, html);
          } catch (err) {
            // Never strand him: offer the real page rather than an empty panel.
            this.quickSlot.innerHTML =
              `<p class="tb-quick__loading">Couldn't load the details. `
              + `<a href="${window.Shopify.routes.root}products/${handle}" target="_blank" rel="noopener">Open the product page</a>.</p>`;
            return;
          }
        }
        if (this.quick.hidden) return;
        this.quickSlot.innerHTML = html;
        this.quickSlot.scrollTop = 0;

        const raw = this.quickSlot.querySelector('[data-tb-quick-variants]');
        this.quickVariants = raw ? this.parse(raw.textContent, []) : [];
        // Start the drawer on whatever he already had chosen on the tile.
        const chosen = Array.from(tile.querySelectorAll('[data-tb-option]')).map((sel) => sel.value);
        this.quickSlot.querySelectorAll('[data-tb-quick-option]').forEach((sel, i) => {
          if (chosen[i] != null && Array.from(sel.options).some((o) => o.value === chosen[i])) {
            sel.value = chosen[i];
          }
          sel.addEventListener('change', () => this.paintQuick());
        });
        this.quickFoot.hidden = !this.quickSlot.querySelector('[data-tb-quick-buy]');
        this.wakeReviews();
        this.paintQuick();
        this.quick.querySelector('.tb-quick__x')?.focus();
      }

      // Judge.me renders its widget from its own script. Dropped into the DOM
      // after load it needs a poke, and the poke is not the same call in every
      // version — so try what exists, then check whether anything actually
      // appeared. An empty reviews box is worse than admitting there isn't one.
      wakeReviews() {
        const box = this.quickSlot.querySelector('[data-tb-quick-reviews]');
        const slot = box?.querySelector('.jdgm-widget');
        if (!box || !slot) return;
        const jdgm = window.jdgm || {};
        [jdgm.batchRenderBadges, jdgm.customizeBadges, jdgm.initWidgets,
         window.jdgmSetupWidgets, window.jdgmLoadCss]
          .forEach((fn) => { try { if (typeof fn === 'function') fn.call(jdgm); } catch (err) { /* not this one */ } });

        clearTimeout(this.reviewTimer);
        this.reviewTimer = setTimeout(() => {
          if (slot.children.length || slot.textContent.trim()) return;
          const fallback = box.querySelector('[data-tb-quick-reviews-fallback]');
          slot.replaceWith(fallback ? fallback.content.cloneNode(true) : document.createTextNode(''));
        }, 1800);
      }

      quickVariant() {
        const chosen = Array.from(this.quickSlot.querySelectorAll('[data-tb-quick-option]')).map((s) => s.value);
        return (this.quickVariants || []).find((v) => v.options.every((o, i) => o === chosen[i]));
      }

      paintQuick() {
        if (!this.quickAdd) return;
        const variant = this.quickVariant();
        const out = !variant || !variant.available;
        this.quickAdd.disabled = out;
        const line = variant ? this.lines.get(String(variant.id)) : null;
        const mine = line ? line.qty : 0;
        this.quickAdd.textContent = out
          ? 'Sold out'
          : mine > 0 ? `Add another (${mine} in pack)` : 'Add to pack';
        // A cue that this is one stop on a bigger job, not the end of it.
        const meterEl0 = this.quick.querySelector('[data-tb-quick-meter]');
        if (meterEl0) meterEl0.classList.toggle('is-live', this.lines.size > 0);

        // The meter is behind the drawer, so bring its state along.
        const meterEl = this.quick.querySelector('[data-tb-quick-meter]');
        const msg = this.querySelector('[data-tb-meter-msg]');
        if (meterEl && msg) meterEl.textContent = msg.textContent.trim();
      }

      addFromQuick() {
        const variant = this.quickVariant();
        if (!variant || !variant.available || !this.quickTile) return;
        // Keep the tile in step with what he chose in the drawer.
        const chosen = Array.from(this.quickSlot.querySelectorAll('[data-tb-quick-option]')).map((s) => s.value);
        const tile = this.quickTile;
        tile.querySelectorAll('[data-tb-option]').forEach((sel, i) => {
          if (chosen[i] != null && Array.from(sel.options).some((o) => o.value === chosen[i])) {
            sel.value = chosen[i];
          }
        });
        this.add(tile);
        // Adding closes the drawer. Leaving it open is how someone ends up
        // reading about tees and forgetting there are three other things — the
        // way out has to be the same gesture as the way forward.
        this.closeQuick();
        // Land him back on the grid looking at the tile he just filled, with the
        // meter in view, so the reward for adding is the first thing he sees.
        const meter = this.querySelector('[data-tb-meter]');
        if (meter && !this.prefersStill()) {
          meter.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        this.celebrate(this.querySelector('[data-tb-meter-badge]'));
      }

      prefersStill() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      closeQuick() {
        if (!this.quick || this.quick.hidden) return;
        if (this.quickPanel) {
          this.quickPanel.style.transition = '';
          this.quickPanel.style.transform = '';
        }
        const scrim = this.quick.querySelector('.tb-quick__scrim');
        if (scrim) scrim.style.opacity = '';
        this.quick.hidden = true;
        document.body.classList.remove('tb-quick-on');
        this.quickReturn?.focus();
        this.quickTile = null;
      }

      // ---- the pack survives a reload ----------------------------------
      savePack() {
        if (!this.lines.size) {
          TB_STORE.drop(this.packKey);
          return;
        }
        let units = 0;
        this.lines.forEach((line) => { units += line.qty; });
        TB_STORE.write(this.packKey, {
          at: Date.now(),
          // Where it lives and how big it is, so the bag can point back at it
          // without having to know anything about this page.
          url: window.location.pathname,
          units,
          lines: Array.from(this.lines.values()).map((line) => ({ id: line.id, qty: line.qty })),
        });
      }

      // Arriving from a product page. The link carries that product's handle,
      // so the builder opens with it already in the pack and the meter already
      // moving. Landing in an empty builder after clicking "Build a pack" reads
      // as being sent back to the start, and people leave.
      //
      // The handle is only ever matched against tiles this page already
      // rendered, so a link cannot introduce a product, a price or a quantity —
      // the worst a bad handle can do is nothing at all.
      seedFromLink() {
        let wanted = [];
        try {
          const raw = new URLSearchParams(window.location.search).get('add');
          if (!raw) return;
          wanted = raw.split(',').map((h) => h.trim().toLowerCase()).filter(Boolean).slice(0, 6);
        } catch (e) {
          return;
        }
        if (!wanted.length) return;

        let seeded = 0;
        wanted.forEach((entry) => {
          // "handle" or "handle:3" — the quantity the product page was showing.
          const bits = entry.split(':');
          const handle = bits[0];
          let qty = parseInt(bits[1], 10);
          if (!Number.isFinite(qty) || qty < 1) qty = 1;
          if (qty > 10) qty = 10;
          const tile = this.tiles.find((t) => (t.dataset.handle || '').toLowerCase() === handle);
          if (!tile) return;
          for (let i = 0; i < qty; i += 1) this.add(tile);
          seeded += qty;
        });

        // Take the parameter back out of the address bar, so a refresh or a
        // shared link does not quietly add the same pack a second time.
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('add');
          window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        } catch (e) {
          /* an old browser keeps the parameter; nothing breaks */
        }

        if (!seeded) return;
        tbTrack('pack_seeded_from_link', { seeded: seeded, lines: this.lines.size });
        // They came here to build, not to read the hero again.
        requestAnimationFrame(() => {
          try {
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } catch (e) {
            this.scrollIntoView();
          }
        });
      }

      restorePack() {
        const saved = TB_STORE.read(this.packKey);
        if (!saved || !Array.isArray(saved.lines)) return;
        // A week-old pack is a stale price, not a helpful memory.
        if (!saved.at || Date.now() - saved.at > 7 * 24 * 60 * 60 * 1000) {
          TB_STORE.drop(this.packKey);
          return;
        }

        let restored = 0;
        saved.lines.forEach((entry) => {
          const qty = Number(entry.qty);
          if (!Number.isFinite(qty) || qty < 1) return;
          // Only put back things this page can still actually sell.
          for (let i = 0; i < this.tiles.length; i += 1) {
            const tile = this.tiles[i];
            const variant = (tile.variants || []).find((v) => v.id === entry.id);
            if (!variant) continue;
            if (!variant.available) break;
            this.lines.set(String(variant.id), {
              id: variant.id,
              qty,
              price: variant.price,
              productId: tile.dataset.productId,
              counts: this.eligibleAll
                || (tile.dataset.counts !== 'false' && this.eligible.has(Number(tile.dataset.productId))),
              title: this.shortTitle(tile),
              detail: this.lineDetail(tile, variant.options),
            });
            // Put the pickers back where they were, so the tile agrees with the pack.
            tile.querySelectorAll('[data-tb-option]').forEach((sel, index) => {
              if (variant.options[index] != null) sel.value = variant.options[index];
            });
            restored += 1;
            break;
          }
        });
        if (restored) tbTrack('pack_restored', { lines: restored });
      }

      // ---- he is a Large on every tile, not just the first one ----------
      rememberSize(tile) {
        const map = this.sizes || {};
        tile.querySelectorAll('[data-tb-option]').forEach((sel) => {
          const name = this.optionName(sel);
          if (name) map[name] = sel.value;
        });
        this.sizes = map;
        TB_STORE.write(this.sizeKey, map);
      }

      restoreSizes() {
        const map = TB_STORE.read(this.sizeKey);
        if (!map || typeof map !== 'object') return;
        this.sizes = map;
        this.tiles.forEach((tile) => this.applySizes(tile));
      }

      applySizes(tile) {
        if (!this.sizes) return;
        const pickers = Array.from(tile.querySelectorAll('[data-tb-option]'));
        const before = pickers.map((sel) => sel.value);
        let changed = false;
        pickers.forEach((sel) => {
          const want = this.sizes[this.optionName(sel)];
          if (!want) return;
          // Only if this product actually offers it — never invent a choice.
          const has = Array.from(sel.options).some((o) => o.value === want);
          if (has && sel.value !== want) {
            sel.value = want;
            changed = true;
          }
        });
        if (!changed) return;
        // Remembering his size must never make a tile read "Sold out" before he
        // has touched it. If the combination we just built cannot be bought,
        // put the tile back exactly as it was.
        const variant = this.currentVariant(tile);
        if (!variant || !variant.available) {
          pickers.forEach((sel, i) => { sel.value = before[i]; });
          return;
        }
        this.onOptionChange(tile, true);
      }

      optionName(sel) {
        return (sel.closest('[data-tb-option-name]')?.dataset.tbOptionName
          || sel.previousElementSibling?.textContent
          || '').trim().toLowerCase();
      }

      pctAt(gross) {
        let hit = 0;
        this.tiers.forEach((t) => {
          if (gross >= t.at) hit = t.pct;
        });
        return hit;
      }

      // The gross subtotal at which shipping actually becomes free, given that a
      // discount is taken off before Shopify looks at the shipping threshold.
      grossForFreeShipping() {
        let gross = this.freeCents;
        for (let i = 0; i < 8; i += 1) {
          const pct = this.pctAt(gross);
          const need = Math.ceil(this.freeCents / (1 - pct / 100));
          if (need === gross) break;
          gross = need;
        }
        return gross;
      }

      fillPercent(counted) {
        const stops = this.stops || [];
        if (!stops.length) return 0;
        let previous = 0;
        for (let i = 0; i < stops.length; i += 1) {
          const stop = stops[i];
          if (counted < stop.at) {
            const span = stop.at - previous;
            const through = span > 0 ? (counted - previous) / span : 1;
            return (i + Math.max(0, Math.min(1, through))) * this.segment;
          }
          previous = stop.at;
        }
        return 100;
      }

      buildStops() {
        this.stops = this.tiers.map((t) => ({
          // "off" rides along even on the narrow label. A bare "10%" beside a
          // dollar figure is a number without a verb — it could as easily be
          // read as a share of the pack as a discount on it.
          at: t.at, label: `${t.pct}% off`, short: `${t.pct}% off`, kind: 'pct', pct: t.pct,
        }));
        if (this.mode === 'value' && this.freeCents) {
          this.stops.push({
            at: this.grossForFreeShipping(), label: 'Free shipping', short: 'Free shipping', kind: 'ship',
          });
        }
        this.stops.sort((a, b) => a.at - b.at);
        this.max = this.stops.length ? this.stops[this.stops.length - 1].at : 1;
        // Every checkpoint gets an equal share of the bar, so each step feels like
        // the same distance however far apart the dollar amounts really are.
        this.segment = this.stops.length ? 100 / this.stops.length : 100;

        const host = this.querySelector('[data-tb-stops]');
        if (!host) return;
        host.innerHTML = this.stops
          .map((stop, i) => {
            const left = (i + 1) * this.segment;
            const detail = this.mode === 'value' ? this.money(stop.at) : `${stop.at} packs`;
            return `<span class="tb-meter__stop tb-meter__stop--${stop.kind}" data-tb-stop="${i}" style="left:${left}%">
              <i></i><em><b class="tb-meter__lg">${stop.label}</b><b class="tb-meter__sm">${stop.short}</b><br><small>${detail}</small></em>
            </span>`;
          })
          .join('');
      }

      parse(value, fallback) {
        try {
          return JSON.parse(value);
        } catch (err) {
          return fallback;
        }
      }

      async readBag() {
        try {
          const res = await fetch(`${window.Shopify.routes.root}cart.js`);
          const cart = await res.json();
          const mine = (cart.items || []).filter(
            (item) => this.eligibleAll || this.eligible.has(Number(item.product_id))
          );
          this.inBag = mine.reduce((sum, item) => sum + item.quantity, 0);
          this.inBagValue = mine.reduce((sum, item) => sum + item.line_price, 0);
        } catch (err) {
          this.inBag = 0;
          this.inBagValue = 0;
        }
        this.render();
      }

      makeDebug() {
        const forced = window.location.search.indexOf('tbdebug=1') !== -1;
        if (!forced && !(window.Shopify && window.Shopify.designMode)) return;
        this.debug = document.createElement('div');
        this.debug.className = 'tb-debug';
        const meter = this.querySelector('[data-tb-meter]');
        if (meter && meter.parentNode) {
          meter.parentNode.insertBefore(this.debug, meter.nextSibling);
        } else {
          this.appendChild(this.debug);
        }
      }

      writeDebug(counted, span) {
        if (!this.debug) return;
        const rows = [
          ['build', TB_BUILD],
          ['meter mode', this.mode],
          ['eligible ids', this.eligible.size + (this.eligibleAll ? '  (empty list → everything counts)' : '')],
          ['checkpoints', this.tiers.length ? this.tiers.map((t) => `${t.at}c=${t.pct}%`).join('  ') : 'NONE — no percent set'],
          ['free shipping', this.freeCents ? this.freeCents + 'c' : 'NOT SET'],
          ['markers on bar', (this.stops || []).map((s2) => s2.at + 'c ' + s2.label).join('  |  ') || 'NONE'],
          ['bar runs 0 →', span + 'c'],
          ['lines in pack', this.lines.size],
          ['counted', counted + 'c'],
          ['bar width', span > 0 ? Math.round((counted / span) * 1000) / 10 + '%' : 'n/a'],
          ['bar painted', this.trackEl ? getComputedStyle(this.trackEl).backgroundSize : 'NO TRACK'],
          ['bar colour', this.trackEl ? getComputedStyle(this.trackEl).backgroundImage.slice(0, 60) : 'n/a'],
        ];
        this.debug.innerHTML =
          '<p class="tb-debug__h">Builder readout — only you can see this, it disappears on the live site</p>' +
          rows.map((r) => `<div><span>${r[0]}</span><b>${r[1]}</b></div>`).join('');
      }

      // Crossing a checkpoint is the one moment on this page worth marking, and
      // it used to pass with a 14% nudge of the badge and a border flash on a
      // bar that has no visible border. Three cues now, all inside a second:
      // the bar catches a light, the dot just passed throws a ring, the badge
      // lands. Restrained, but unmistakably something happened.
      celebrate(el) {
        if (this.prefersStill()) return;
        const restart = (node, cls) => {
          if (!node) return;
          node.classList.remove(cls);
          // Reading the layout forces the removal to take effect, so the same
          // animation can be played twice in a row.
          void node.offsetWidth;
          node.classList.add(cls);
        };
        restart(el, 'tb-pop');
        restart(this.querySelector('.tb-meter__track'), 'tb-sweep');
        restart(this.querySelector('[data-tb-meter]'), 'tb-won');
        const lit = this.querySelectorAll('.tb-meter__stop.is-on');
        restart(lit[lit.length - 1], 'tb-hit');

        // A short double tap of the phone, where the phone allows it. Android
        // does; iOS Safari has no vibration API at all, so roughly half the
        // traffic feels nothing and the visual has to carry the moment on its
        // own. Wrapped because some browsers expose the method and then throw.
        try {
          if (navigator.vibrate) navigator.vibrate([12, 40, 22]);
        } catch (err) {
          /* no haptics here */
        }
      }

      money(cents) {
        const value = Math.round(cents) / 100;
        if (!this.formatter) {
          try {
            this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
              style: 'currency',
              currency: this.currency,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          } catch (err) {
            this.formatter = null;
          }
        }
        if (this.formatter) return this.formatter.format(value);
        return `$${value.toFixed(2)}`;
      }

      // A tile's price, in the terms the pack is currently being bought on.
      // Writes to the amount only — setting text on the whole paragraph is what
      // used to erase the Subscribe chip and the Full price tag.
      paintTilePrice(tile) {
        const el = tile.querySelector('[data-tb-price-amount]');
        if (!el) return;
        const variant = this.currentVariant(tile);
        const list = variant ? variant.price : Number(
          tile.querySelector('[data-tb-price]')?.dataset.cents || 0
        );
        // The card shows one price: what this costs. The chip beside it is a
        // standing offer, not a readout of the current pack — a tile that
        // silently reprices itself because of a switch further down the page
        // is a card you cannot trust at a glance.
        el.textContent = this.money(list);

        // It is a control now, so it says what state it put things in. A
        // button that swallows a press without visibly changing gets pressed
        // again, and again.
        const chip = tile.querySelector('[data-tb-subchip]');
        if (chip) {
          const v = this.currentVariant(tile);
          const line = v ? this.lines.get(String(v.id)) : null;
          const on = Boolean(line && this.repeats(line));
          chip.classList.toggle('is-on', on);
          if (!chip.dataset.label) chip.dataset.label = chip.textContent.trim();
          chip.textContent = on ? 'Subscribed' : chip.dataset.label;
        }
      }

      // The grid needs the full title — it is the product page's headline and it
      // is what search reads. A summary line does not: six of those stacked is
      // most of a phone screen spent on words the shopper already knows.
      //
      // Tag a product short:Tees to set this properly. The guess below is a
      // convenience so the cart is readable before any tagging is done, and it
      // never overrides a tag.
      // Shortening the name took "(3 Pack)" out of the summary with it, and
      // people already arrive thinking a pack is assembled from singles. It
      // goes back in beside the colour and size, where it is read as a fact
      // about the line rather than part of a long title.
      lineDetail(tile, options) {
        const opts = options
          || Array.from(tile.querySelectorAll('[data-tb-option]')).map((s) => s.value);
        const bits = /3[\s-]?pack/i.test(tile.dataset.title || '') ? ['3-pack'] : [];
        return bits.concat(opts).join(' · ');
      }

      shortTitle(tile) {
        if (tile.dataset.short) return `TRAILBLAZER ${tile.dataset.short}`;
        const full = tile.dataset.title
          || tile.querySelector('.tb-card__title')?.textContent.trim() || '';
        return tbShortName(full);
      }

      currentVariant(tile) {
        const chosen = Array.from(tile.querySelectorAll('[data-tb-option]')).map((s) => s.value);
        return tile.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
      }

      onOptionChange(tile, quiet) {
        // quiet = we set the dropdown ourselves, so do not echo it back out.
        if (!quiet) {
          this.rememberSize(tile);
          this.tiles.forEach((other) => {
            if (other !== tile) this.applySizes(other);
          });
        }
        const variant = this.currentVariant(tile);
        this.paintTilePrice(tile);
        const button = tile.querySelector('[data-tb-add]');
        if (button) {
          const out = !variant || !variant.available;
          button.disabled = out;
          button.textContent = out ? 'Sold out' : button.dataset.label || button.textContent;
        }
        tile.classList.toggle('tb-bundle__tile--unavailable', !variant || !variant.available);
        // A different size/colour is a different line, so the tile's quantity
        // control has to follow the switch.
        this.render();
      }

      add(tile) {
        const variant = this.currentVariant(tile);
        if (!variant || !variant.available) return;
        const key = String(variant.id);
        const existing = this.lines.get(key);
        if (existing) {
          existing.qty += 1;
        } else {
          this.lines.set(key, {
            id: variant.id,
            qty: 1,
            price: variant.price,
            productId: tile.dataset.productId,
            counts: this.eligibleAll
              || (tile.dataset.counts !== 'false' && this.eligible.has(Number(tile.dataset.productId))),
            title: this.shortTitle(tile),
            detail: this.lineDetail(tile),
          });
        }
        tile.classList.add('tb-bundle__tile--on');
        this.rememberSize(tile);
        this.savePack();
        tbTrack('add_to_pack', {
          variant_id: variant.id,
          product_title: tile.dataset.title || '',
          price: variant.price / 100,
          quantity: this.lines.get(key).qty,
          pack_lines: this.lines.size,
        });
        this.render();
      }

      step(key, delta) {
        const line = this.lines.get(String(key));
        if (!line || !Number.isFinite(delta)) return;
        const next = line.qty + delta;
        if (!Number.isFinite(next) || next < 1) {
          this.lines.delete(String(key));
        } else {
          line.qty = next;
        }
        this.savePack();
        tbTrack(delta > 0 ? 'add_to_pack' : 'remove_from_pack', {
          variant_id: line.id,
          product_title: line.title,
          price: line.price / 100,
          quantity: Math.max(0, next),
          pack_lines: this.lines.size,
        });
        this.render();
      }

      counts() {
        let packs = 0;
        let subtotal = 0;
        let eligibleSubtotal = 0;
        this.lines.forEach((line) => {
          const amount = line.qty * line.price;
          subtotal += amount;
          if (line.counts) {
            packs += line.qty;
            eligibleSubtotal += amount;
          }
        });
        return { packs, subtotal, eligibleSubtotal };
      }

      // What the pack costs on the plan. Every line that HAS a plan switches to
      // its per-delivery price; a product with no plan stays at full price and
      // is simply bought once, so a mixed pack still works.
      //
      // The pack discount is only folded in when the theme setting says the
      // Shopify discount is set to cover subscriptions as well as one-time
      // orders. Left off, the page quotes the higher number and checkout can
      // only ever come in cheaper — the page is never the one making a promise
      // it cannot keep.
      // What the ladder is actually measured against once a subscription is on.
      //
      // Shopify takes the subscription off the lines FIRST, then decides which
      // rung the cart has reached from what is left. Three tee 3-packs are
      // $80.97 — the $80 rung — but subscribed they are $72.87, which is the
      // $50 rung. The page used to pick the rung from list prices and then
      // apply it to the discounted lines, so it promised a rung checkout was
      // never going to give.
      subEligible() {
        let eligible = 0;
        this.lines.forEach((line) => {
          if (!line.counts) return;
          const plan = this.repeats(line);
          const repeat = plan ? this.repeatQtyFor(line) : 0;
          const today = Math.max(line.qty, repeat);
          const once = today - repeat;
          eligible += once * line.price + repeat * (plan ? plan.price : 0);
        });
        return eligible;
      }

      subTotals(pct) {
        let subtotal = 0;
        let eligible = 0;
        let planned = 0;
        this.lines.forEach((line) => {
          const plan = this.repeats(line);
          // A subscription's first delivery IS today's order, so a line can be
          // part one-off and part repeating. Today's charge is both halves.
          const repeat = plan ? this.repeatQtyFor(line) : 0;
          const today = Math.max(line.qty, repeat);
          const once = today - repeat;
          const amount = once * line.price + repeat * (plan ? plan.price : 0);
          if (repeat > 0) planned += repeat;
          subtotal += amount;
          // Until Shopify is proved to give the pack discount on subscription
          // lines, only the part of the pack that is NOT repeating earns it.
          //
          // This used to zero the pack discount for the WHOLE pack the moment
          // one switch went on, which is why subscribing made the total jump:
          // one subscribed item cost the socks and the tanks their discount
          // too. Nothing in Shopify works that way — a one-time line is a
          // one-time line whatever the line above it is doing.
          if (!line.counts) return;
          if (this.stackDiscount) {
            eligible += amount;
          } else {
            eligible += once * line.price;
          }
        });
        const discount = Math.round((eligible * pct) / 100);
        return { subtotal: subtotal, discount: discount, after: subtotal - discount, planned: planned };
      }

      // The next rung down from this one, or null at the bottom.
      tierBelow(tier) {
        let below = null;
        this.tiers.forEach((t) => {
          if (t.at < tier.at) below = t;
        });
        return below;
      }

      tierFor(progress) {
        let hit = null;
        this.tiers.forEach((t) => {
          if (progress >= t.at) hit = t;
        });
        return hit;
      }

      nextTier(progress) {
        return this.tiers.find((t) => progress < t.at) || null;
      }

      render() {
        // Emptying the pack empties the subscription with it. Clearing a pack
        // is somebody starting over, and a builder that still said "Set up
        // subscription" over an empty pack was answering a question they had
        // already taken back. Only fires on the way from something to nothing,
        // so it can never fight a choice being made right now.
        if (this.lines.size) {
          this.hadLines = true;
        } else if (this.hadLines) {
          this.hadLines = false;
          this.clearSubPrefs();
        }

        const { packs, subtotal, eligibleSubtotal } = this.counts();
        const repeating = this.repeating();
        const ladderBase = repeating && !this.stackDiscount
          ? this.subEligible()
          : eligibleSubtotal;
        let counted = this.mode === 'value'
          ? ladderBase + this.inBagValue
          : packs + this.inBag;
        if (!Number.isFinite(counted)) counted = 0;
        let tier = this.tierFor(counted);

        // The rung the pack has EARNED is measured at list price, which is what
        // the meter shows and what the page promises. Shopify decides
        // separately, and it measures its own minimum after the subscription
        // has come off — so a cart that clears $80 here can arrive at $72.
        //
        // Where the Shopify minimum has been set lower to absorb that, the rung
        // survives and nothing below needs to change. Where it has not, the
        // rung is genuinely lost, and the page steps down to the one that will
        // actually be applied rather than quoting a saving checkout will
        // refuse. No guessing at a buffer: it compares the same two numbers
        // Shopify does.
        if (repeating && this.stackDiscount && this.mode === 'value' && tier) {
          const real = this.subEligible() + this.inBagValue;
          while (tier && real < (tier.min == null ? tier.at : tier.min)) {
            tier = this.tierBelow(tier);
          }
        }
        const next = this.nextTier(counted);
        const pct = tier ? tier.pct : 0;
        const discount = Math.round((eligibleSubtotal * pct) / 100);
        const after = subtotal - discount;

        // Everything downstream — the meter's free-shipping test, the summary,
        // the dock — spends the number the shopper is actually going to be
        // charged, which is a different number once the pack is on a plan.
        const rep = repeating;
        const sub = this.subTotals(pct);
        const eff = rep ? sub.after : after;
        this.paintRepeat(sub, after, subtotal);

        // meter
        const gotFree = Boolean(this.freeCents) && eff >= this.freeCents;
        const span = this.max > 0 ? this.max : 1;
        const width = Math.max(0, Math.min(100, this.fillPercent(counted)));

        // The fill is painted as the track's own background, not as a child
        // element. A background can't be collapsed by a layout quirk, so this
        // works regardless of what the surrounding theme does.
        const track = this.querySelector('.tb-meter__track');
        if (track) {
          track.style.backgroundColor = 'var(--tb-sand, #E7E1D6)';
          track.style.backgroundImage =
            'linear-gradient(var(--tb-ink, #14140F), var(--tb-ink, #14140F))';
          track.style.backgroundRepeat = 'no-repeat';
          track.style.backgroundPosition = 'left center';
          track.style.backgroundSize = `${width}% 100%`;
          this.trackEl = track;
        }
        // The old child element, if the markup still has one, is left out of it.
        const legacyFill = this.querySelector('[data-tb-meter-fill]');
        if (legacyFill) legacyFill.style.display = 'none';

        this.writeDebug(counted, span);
        if (this.debug) {
          clearTimeout(this.debugTimer);
          this.debugTimer = setTimeout(() => this.writeDebug(counted, span), 700);
        }

        const stops = this.stops || [];
        this.querySelectorAll('[data-tb-stop]').forEach((el) => {
          // data-tb-stop reads back as dataset.tbStop, not dataset.stop. With
          // the wrong key every lookup came back undefined and the loop bailed
          // on the line below — which is why the checkpoint dots have never
          // once lit up as the bar passed them.
          const stop = stops[Number(el.dataset.tbStop)];
          if (!stop) return;
          el.classList.toggle('is-on', stop.kind === 'ship' ? gotFree : counted >= stop.at);
        });

        const nextStop = stops.find((stop) => (stop.kind === 'ship' ? !gotFree : counted < stop.at));
        const msg = this.querySelector('[data-tb-meter-msg]');
        if (msg && !this.lines.size && !this.inBag) {
          msg.innerHTML = this.startNote;
        } else if (msg && (this.lines.size || this.inBag)) {
          if (!nextStop) {
            msg.innerHTML = 'Everything unlocked. Add as much as you like.';
          } else if (this.mode === 'value') {
            const gap = nextStop.kind === 'ship'
              ? this.freeCents - eff
              : nextStop.at - counted;
            msg.innerHTML = `<strong>${this.money(Math.max(0, gap))}</strong> more for <strong>${nextStop.label}</strong>`;
          } else {
            const need = Math.max(1, nextStop.at - counted);
            msg.innerHTML = `Add <strong>${need}</strong> more pack${need === 1 ? '' : 's'} for <strong>${nextStop.label}</strong>`;
          }
        }

        const badge = this.querySelector('[data-tb-meter-badge]');
        if (badge) {
          const won = [];
          if (tier) won.push(`${tier.pct}% off`);
          if (gotFree) won.push('free shipping');
          badge.hidden = won.length === 0;
          if (won.length) badge.textContent = `${won.join(' + ')} unlocked`;
        }

        if (pct > this.lastPct || (gotFree && !this.lastFree)) this.celebrate(badge);
        if (pct > this.lastPct) {
          tbTrack('checkpoint_unlocked', {
            checkpoint: `${pct}% off`,
            percent_off: pct,
            pack_value: eligibleSubtotal / 100,
          });
        }
        if (gotFree && !this.lastFree) {
          tbTrack('checkpoint_unlocked', { checkpoint: 'Free shipping', pack_value: eff / 100 });
        }
        this.lastPct = pct;
        this.lastFree = gotFree;

        // lines
        if (this.list) {
          if (!this.lines.size) {
            this.list.innerHTML = '<li class="tb-bundle__empty">Nothing chosen yet.</li>';
          } else {
            this.list.innerHTML = Array.from(this.lines.values())
              .map(
                (line) => this.lineRow(line)
              )
              .join('');
          }
        }

        this.tiles.forEach((tile) => {
          let qty = 0;
          this.lines.forEach((line) => {
            if (line.productId === tile.dataset.productId) qty += line.qty;
          });
          this.paintTilePrice(tile);
          const count = tile.querySelector('[data-tb-count]');
          if (count) {
            count.hidden = qty === 0;
            count.textContent = qty;
          }
          tile.classList.toggle('tb-bundle__tile--on', qty > 0);

          // The stepper shows the chosen variant, not the whole product: the
          // corner badge already carries the product total.
          const addButton = tile.querySelector('[data-tb-add]');
          if (tile.stepper && addButton) {
            const chosen = this.currentVariant(tile);
            const line = chosen ? this.lines.get(String(chosen.id)) : null;
            const mine = line ? line.qty : 0;
            tile.stepper.hidden = mine === 0;
            addButton.hidden = mine > 0;
            const readout = tile.stepper.querySelector('[data-tb-tile-qty]');
            if (readout) readout.textContent = mine;
          }
        });

        // totals
        const totals = this.querySelector('[data-tb-totals]');
        if (totals) totals.hidden = !this.lines.size;
        const subEl = this.querySelector('[data-tb-sub]');
        if (subEl) subEl.textContent = this.money(subtotal);

        const saveRow = this.querySelector('[data-tb-save-row]');
        if (saveRow) {
          const shown = rep ? sub.discount : discount;
          saveRow.hidden = shown === 0;
          const label = this.querySelector('[data-tb-save-label]');
          if (label) label.textContent = `Pack discount (${pct}%)`;
          const save = this.querySelector('[data-tb-save]');
          if (save) save.textContent = `−${this.money(shown)}`;
        }

        const subRow = this.querySelector('[data-tb-subsave-row]');
        if (subRow) {
          const off = subtotal - sub.subtotal;
          subRow.hidden = !rep || off <= 0;
          const cell = this.querySelector('[data-tb-subsave]');
          if (cell) cell.textContent = `−${this.money(off)}`;

          // Name what the 10% actually comes off. Sitting bare as
          // "Subscription" in a column of pack-wide figures, it reads as 10%
          // off the order — which it is not. It is 10% off the items that
          // repeat, and the cheapest place to say so is right here, at the
          // number itself. Same item in two sizes is still one name.
          // Falls back to the row's own <dt> so this works against the markup
          // already on the page — no second file to re-paste for one attribute.
          const subLabel = subRow.querySelector('[data-tb-subsave-label]')
            || subRow.querySelector('dt');
          if (subLabel) {
            const names = [];
            this.lines.forEach((line) => {
              if (!this.repeats(line)) return;
              // "Subscription (TRAILBLAZER Tees, TRAILBLAZER Socks)" is a
              // paragraph. The brand is already all over the page; what this
              // needs to carry is which of the four things it is.
              const nm = String(line.title || '').replace(/^\s*TRAILBLAZER\s+/i, '').trim();
              if (nm && names.indexOf(nm) === -1) names.push(nm);
            });
            subLabel.textContent = names.length
              ? `Subscription (${names.join(', ')})`
              : 'Subscription';
          }
        }

        if (this.quick && !this.quick.hidden) this.paintQuick();

        if (this.dock) {
          let items = 0;
          this.lines.forEach((line) => { items += line.qty; });
          const total = this.dock.querySelector('[data-tb-dock-total]');
          const note = this.dock.querySelector('[data-tb-dock-note]');
          if (total) total.textContent = this.money(eff);
          if (note) {
            const bits = [`${items} pack${items === 1 ? '' : 's'}`];
            if (discount > 0) bits.push(`${pct}% off applied`);
            else if (nextStop && this.mode === 'value') {
              const gap = nextStop.kind === 'ship' ? this.freeCents - eff : nextStop.at - counted;
              bits.push(`${this.money(Math.max(0, gap))} to ${nextStop.label.toLowerCase()}`);
            }
            note.textContent = bits.join(' · ');
          }
          this.paintDock();
        }

        const shipRow = this.querySelector('[data-tb-ship-row]');
        if (shipRow) {
          shipRow.hidden = !this.freeCents || !this.lines.size;
          const ship = this.querySelector('[data-tb-ship]');
          if (ship) {
            if (eff >= this.freeCents) {
              // "Free" on its own left people asking "free what?" — the meter
              // says Free shipping, so the summary says it too.
              ship.textContent = 'Free shipping';
              ship.classList.add('tb-bundle__free');
            } else {
              ship.textContent = `${this.money(this.freeCents - eff)} away from free shipping`;
              ship.classList.remove('tb-bundle__free');
            }
          }
        }

        const totalEl = this.querySelector('[data-tb-total]');
        if (totalEl) totalEl.textContent = this.money(eff);

        if (this.addButton) {
          this.addButton.disabled = this.lines.size === 0;
          this.addButton.classList.toggle('tb-bundle__ready', Boolean(tier));
        }
      }

      // ---- resupply: how often, asked once, applied per line -----------
      //
      // The builder is the landing page for paid traffic, so this is where
      // most people will ever meet the subscription. It is one choice at the
      // summary rather than a control on every tile, because four of them
      // would push the meter and the button off a phone screen.
      //
      // Per line, the repeat quantity is its OWN number and defaults to 1 —
      // never inherited from what they are buying today. A re-up is a top-up.
      wireRepeat() {
        this.repeatBox = this.querySelector('[data-tb-repeat]');
        if (!this.repeatBox) return;
        this.stackDiscount = this.dataset.stackDiscount === 'true';

        // A radio that is already checked fires no change event, so pressing
        // Subscribe while already subscribed did nothing at all — the exact
        // case where somebody wants everything switched back on. Click covers
        // it; change still covers keyboard.
        this.repeatBox.querySelectorAll('.tb-repeat__opt').forEach((el) => {
          el.addEventListener('click', () => {
            const input = el.querySelector('[data-tb-repeat-mode]');
            if (input) this.setRepeat(input.value === 'repeat');
          });
        });

        this.repeatBox.querySelectorAll('[data-tb-repeat-mode]').forEach((el) => {
          el.addEventListener('change', () => {
            // Choosing Subscribe on the card means subscribe, every time it is
            // pressed. It used to leave whatever items had been switched off
            // earlier still switched off, so the pack could say Subscribe with
            // half of it — or none of it — actually repeating.
            if (this.repeating()) this.subOff.clear();
            tbTrack('resupply_mode', { on: this.repeating() });
            this.render();
          });
        });

      }

      // The card carries the numbers, so it has to be repainted like any other
      // total. It stays out of sight until there is a pack to put on repeat —
      // an empty summary quoting $0.00 a month is noise.
      paintRepeat(sub, onceAfter, listTotal) {
        const box = this.repeatBox;
        if (!box) return;

        box.hidden = this.lines.size === 0;
        if (!this.lines.size) return;

        // Held for the dock, which paints on its own schedule as the page
        // scrolls and has no access to these numbers otherwise.
        this.subOffPct = this.subRate();

        // The tag is the offer, not a verdict on the current pack — the same
        // standing fact as the words beside it. It states what subscribing
        // takes off the things that repeat, which is true whether or not
        // anything is switched on yet, so it is always on screen.
        //
        // Liquid prints it from a real plan price. This only sharpens it once
        // there is a pack to measure, and never blanks it.
        const tag = box.querySelector('[data-tb-rep-tag]');
        if (tag && this.repeating()) {
          const off = this.subRate();
          if (off > 0) tag.textContent = `Save ${off}%`;
        }
        void onceAfter;
        void listTotal;
        void sub;

        const note = box.querySelector('[data-tb-repeat-note]');
        if (note) {
          let able = 0;
          let on = 0;
          let units = 0;
          this.lines.forEach((line) => {
            if (!this.hasAnyPlan(line)) return;
            able += 1;
            if (this.repeats(line)) { on += 1; units += this.repeatQtyFor(line); }
          });
          // Four states, four sentences. The one on "Just this once" used to
          // describe the machinery — "10% off anything you keep coming, set
          // per item" — to somebody who had just said they did not want any
          // of it. It sells the offer in one line instead.
          if (!this.repeating()) {
            note.textContent = `Subscribe to any item and save ${this.subOffPct || 10}% on it. `
              + 'Skip or cancel any time.';
          } else if (able === 0) {
            note.textContent = 'None of these can be subscribed to yet.';
          } else if (on === 0) {
            note.textContent = 'Switch on the items you want sent again. '
              + 'You pick how many and how often for each one.';
          } else {
            const every = on === 1 ? 'item' : 'items';
            note.textContent = `${units} pack${units === 1 ? '' : 's'} coming back `
              + `from ${on} ${every}. Skip or cancel any time.`;
          }
        }
      }

      repeating() {
        const on = this.repeatBox?.querySelector('[data-tb-repeat-mode]:checked');
        return Boolean(on) && on.value === 'repeat';
      }

      // This product's own plan for the CADENCE on screen. Each product carries
      // its own plan ids even when the cadences are worded identically, so the
      // match is on "Every 3 months", not on an id borrowed from whichever
      // product happened to be first in the collection.
      //
      // Returns null when this product has no plan at that cadence, which is a
      // normal state — that item is simply bought once.
      // This variant's plan for the cadence on screen.
      planOn(variant, every) {
        if (!variant || !variant.plans || !every) return null;
        const map = this.cadences || {};
        const ids = Object.keys(variant.plans);
        const hit = ids.find((id) => map[id] === every);
        if (hit) return { id: hit, price: variant.plans[hit] };
        // Older markup put plan IDS in the picker rather than cadences. Match
        // those too, so a half-updated theme degrades to "works" rather than
        // to "nothing in this pack can repeat".
        if (variant.plans[every] != null) return { id: every, price: variant.plans[every] };
        // No map (or a cadence this product does not run) — one plan and one
        // cadence on offer is not ambiguous, so use it rather than telling the
        // shopper nothing can repeat.
        if (ids.length === 1 && Object.keys(map).length <= 1) {
          return { id: ids[0], price: variant.plans[ids[0]] };
        }
        // A cadence this product does not run. It still has a plan, so it can
        // still repeat — on the nearest thing it does offer.
        if (ids.length) return { id: ids[0], price: variant.plans[ids[0]] };
        return null;
      }

      // What the plan actually takes off, measured on the things that repeat.
      //
      // This used to divide the pack's total saving by the pack's whole list
      // value, which asks two different questions at once: one subscribed item
      // out of three reported "Save 6%" when the plan takes 10% off that item.
      // The pack is not on the offer — the subscribed lines are — so numerator
      // and denominator have to describe the same lines. With nothing switched
      // on there is nothing to measure and Liquid's printed rate, which comes
      // from a real plan price, is left alone.
      subRate() {
        let list = 0;
        let plan = 0;
        this.lines.forEach((line) => {
          const p = this.repeats(line);
          if (!p) return;
          const n = this.repeatQtyFor(line);
          list += n * line.price;
          plan += n * p.price;
        });
        if (list <= 0) return 0;
        return Math.round(((list - plan) / list) * 100);
      }

      repeats(line) {
        if (!this.repeating()) return null;
        if (this.subOff.has(String(line.id))) return null;
        return this.planFor(line);
      }

      // Starts at one. A re-up is a top-up: somebody buying three today is
      // stocking a drawer, not committing to three of the same thing forever,
      // and the number they were shown is the one they are agreeing to.
      repeatQtyFor(line) {
        const key = String(line.id);
        let n = this.repeatQty.has(key) ? this.repeatQty.get(key) : 1;
        if (n < 1) n = 1;
        if (n > 10) n = 10;
        return n;
      }

      // Each line keeps its own cadence. Socks every month and tees every three
      // is a real pack, and one picker at the top could not describe it.
      everyFor(line) {
        const key = String(line.id);
        return this.repeatEvery.has(key) ? this.repeatEvery.get(key) : this.defaultEvery;
      }

      planFor(line) {
        for (let i = 0; i < this.tiles.length; i += 1) {
          const variant = (this.tiles[i].variants || []).find((v) => String(v.id) === String(line.id));
          if (variant) return this.planOn(variant, this.everyFor(line));
        }
        return null;
      }

      repeatPrice(line) {
        const plan = this.planFor(line);
        return plan ? plan.price : null;
      }

      // The switch rides on the item's own line, on the right, because it is
      // a fact about that item — not a separate row asking a separate
      // question. Turning it on opens a second line underneath with that
      // item's own quantity and its own cadence, on the same rules as the
      // product page: the first delivery is today's order, so raising the
      // subscription raises today rather than being capped by it.
      lineRow(line) {
        const name = `<span class="tb-bundle__line-name">${line.title}${line.detail ? `<small>${line.detail}</small>` : ''}</span>`;
        const step = (dir, attr, variant, label) =>
          `<button type="button" ${attr}="${dir}" data-variant="${variant}" aria-label="${label}">${dir < 0 ? '&minus;' : '+'}</button>`;

        const qty = `<span class="tb-bundle__step">
            ${step(-1, 'data-tb-step', line.id, 'Remove one')}
            <b>${line.qty}</b>
            ${step(1, 'data-tb-step', line.id, 'Add one')}
          </span>`;
        const amount = `<span class="tb-bundle__line-amt">${this.money(line.qty * line.price)}</span>`;

        // The switch is on every line that COULD repeat, whichever way the
        // pack is set — because it is also the way in. Somebody on "Just this
        // once" who flips one item has said exactly what they want, and making
        // them find the master card first would be asking them to say it
        // twice.
        const key = String(line.id);
        const can = this.hasAnyPlan(line);
        const on = can && this.repeating() && !this.subOff.has(key);

        let sw = '';
        if (can) {
          sw = `<span class="tb-bundle__subsw">
            <span class="tb-bundle__subsw-lab">Subscribe</span>
            <button type="button" class="tb-sw${on ? ' is-on' : ''}" role="switch"
              aria-checked="${on}" data-tb-line-sub="${line.id}"
              aria-label="Subscribe to ${line.title}"><i></i></button>
          </span>`;
        } else if (this.repeating()) {
          // Only worth saying while subscribing is on the table. A one-time
          // buyer does not need to be told which items have no plan.
          sw = '<span class="tb-bundle__subsw tb-bundle__subsw--none">No subscription</span>';
        }
        const main = `<div class="tb-bundle__line-main">${name}${qty}${amount}${sw}</div>`;

        if (!on) return `<li class="tb-bundle__line">${main}</li>`;

        const plan = this.planFor(line);
        const n = this.repeatQtyFor(line);
        const mine = this.everyFor(line);
        const options = this.everyNames
          .map((nm) => `<option value="${nm}"${nm === mine ? ' selected' : ''}>${nm}</option>`)
          .join('');

        const listPrice = this.money(n * line.price);
        const subPrice = plan ? this.money(n * plan.price) : '';
        const packs = `${n} pack${n === 1 ? '' : 's'}`;
        const said = (line.detail || '').replace(/^3-pack · ?/, '') || line.title;
        return `<li class="tb-bundle__line tb-bundle__line--on">${main}
          <div class="tb-bundle__subrow">
            <div class="tb-bundle__subrow-head">
              <span class="tb-bundle__subrow-lab">Subscription</span>
              <span class="tb-bundle__subprice">${
                plan ? `<s>${listPrice}</s> ${subPrice}` : '&mdash;'
              }</span>
            </div>
            <div class="tb-bundle__subrow-controls">
              <span class="tb-bundle__field">
                <span class="tb-bundle__field-lab">How many</span>
                <span class="tb-bundle__step tb-bundle__step--sub">
                  ${step(-1, 'data-tb-rep-step', key, 'One fewer on the subscription')}
                  <b>${n}</b>
                  ${step(1, 'data-tb-rep-step', key, 'One more on the subscription')}
                </span>
              </span>
              <span class="tb-bundle__field">
                <span class="tb-bundle__field-lab">Choose how often</span>
                <select class="tb-bundle__subevery" data-tb-line-every="${line.id}"
                  aria-label="How often ${line.title} comes back">${options}</select>
              </span>
            </div>
            <p class="tb-bundle__subnote">${packs} of ${said}, ${
              mine.toLowerCase()
            }. Skip or cancel any time.</p>
          </div>
        </li>`;
      }

      // Whether this product has a plan at ANY cadence. A line whose own
      // cadence is one this product does not run still belongs on screen with
      // its switch — it just needs a different frequency picked.
      hasAnyPlan(line) {
        for (let i = 0; i < this.tiles.length; i += 1) {
          const variant = (this.tiles[i].variants || []).find((v) => String(v.id) === String(line.id));
          if (variant) return Boolean(variant.plans && Object.keys(variant.plans).length);
        }
        return false;
      }

      // Removes the lines a previous pack left behind, and stamps the cart so
      // the order can be tagged. A failure here is silent on purpose: a cart we
      // could not read is a cart we should not start deleting from, and adding
      // to a slightly wrong bag beats emptying a right one.
      async dropOwnLines() {
        try {
          const res = await fetch(`${window.Shopify.routes.root}cart.js`, {
            headers: { Accept: 'application/json' },
          });
          const cart = await res.json();
          const updates = {};
          (cart.items || []).forEach((item) => {
            const props = item.properties || {};
            if (props._pack) updates[item.key] = 0;
          });
          const body = { attributes: { 'Built with': TB_PACK_MARK } };
          if (Object.keys(updates).length) body.updates = updates;
          await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
        } catch (err) {
          /* leave the bag exactly as it was */
        }
      }

      async addAll() {
        // The pack goes over as it was built: one cart line per product. On
        // subscribe, every line that HAS a plan carries it — no splitting a
        // line into a one-off half and a repeating half.
        const items = [];
        this.lines.forEach((line) => {
          const plan = this.repeats(line);
          const repeat = plan ? this.repeatQtyFor(line) : 0;
          const today = Math.max(line.qty, repeat);
          const once = today - repeat;
          // Every line the builder puts in the bag is stamped. The stamp is
          // what lets a second trip through checkout replace this pack instead
          // of piling on top of it, and it is what tells the order, later, that
          // it came from here. Underscore-prefixed, so the shopper never sees
          // it in the cart.
          const mark = { _pack: TB_PACK_MARK };
          if (once > 0) items.push({ id: line.id, quantity: once, properties: mark });
          if (repeat > 0) {
            items.push({
              id: line.id, quantity: repeat, selling_plan: Number(plan.id), properties: mark,
            });
          }
        });
        if (!items.length) return;
        const totals = this.counts();
        const earned = this.pctAt(this.mode === 'value'
          ? totals.eligibleSubtotal + this.inBagValue
          : totals.packs + this.inBag);
        tbTrack('pack_added_to_cart', {
          pack_lines: items.length,
          pack_units: items.reduce((sum, i) => sum + i.quantity, 0),
          value: totals.subtotal / 100,
          percent_off: earned,
        });
        const label = this.addButton.textContent;
        this.addButton.disabled = true;
        this.addButton.textContent = 'Adding…';
        try {
          // THE bug this fixes: checkout used to append. Build a pack, press
          // Check out, change your mind, come back, rebuild, press it again —
          // and the bag held both packs. An $89 order arrived at checkout as
          // $200 with no way to undo it from inside checkout.
          //
          // The pack is presented as the cart, so it has to behave like one:
          // this trip REPLACES the last one. Only lines this builder added are
          // removed — anything picked up on a product page is left alone.
          await this.dropOwnLines();
          const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify({
              items,
              sections: 'cart-drawer,cart-icon-bubble',
              sections_url: window.location.pathname,
            }),
          });
          const data = await res.json();
          // Straight to checkout skips the cart drawer, and with it the free
          // shipping meter and the cross-sell rows — which on a builder page
          // are doing real work. Off by default for that reason; the setting
          // exists so it can be tested rather than argued about.
          if (this.dataset.checkoutDirect === 'true') {
            this.lines.clear();
            TB_STORE.drop(this.packKey);
            window.location.href = window.Shopify.routes.root + 'checkout';
            return;
          }
          const drawer = document.querySelector('cart-drawer');
          if (drawer && data.sections && typeof drawer.renderContents === 'function') {
            drawer.renderContents(data);
            drawer.open?.();
            this.lines.clear();
            TB_STORE.drop(this.packKey);
            this.inBag = 0;
            this.inBagValue = 0;
            if (this.dataset.countCart === 'true') this.readBag();
          } else {
            window.location.href = `${window.Shopify.routes.root}cart`;
            return;
          }
        } catch (err) {
          window.location.href = `${window.Shopify.routes.root}cart`;
          return;
        } finally {
          this.addButton.disabled = false;
          this.addButton.textContent = label;
          this.render();
        }
      }
    }
  );
}

// ---------------------------------------------------------------------------
// The pack line on a product page.
//
// It has to agree with the quantity picker sitting right above it. Someone who
// has already dialled the quantity to 2 does not want to be told to add a
// second pack — they added it — and the link must carry those 2 across, or the
// builder opens holding less than the product page was holding, which reads as
// the site losing their choice.
//
// Prices come from data attributes rendered by Liquid, so the arithmetic here
// is the same arithmetic the server did; only the quantity is live.
// ---------------------------------------------------------------------------
function tbPackCta() {
  var box = document.querySelector('[data-tb-packcta]');
  if (!box) return;
  var line = box.querySelector('[data-tb-packcta-line]');
  var go = box.querySelector('[data-tb-packcta-go]');
  if (!line || !go) return;

  var price = Number(box.dataset.price) || 0;
  var first = Number(box.dataset.first) || 0;
  var pct = Number(box.dataset.pct) || 0;
  var handle = box.dataset.handle || '';
  var url = box.dataset.url || '';
  if (!price || !first || !handle || !url) return;

  var money = function (cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: box.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  // Dawn's stepper writes to this input and fires change; typing fires input.
  var field = document.querySelector('form[action*="/cart/add"] input[name="quantity"]')
    || document.querySelector('input[name="quantity"]');

  var paint = function () {
    var qty = 1;
    if (field) {
      qty = parseInt(field.value, 10);
      if (!Number.isFinite(qty) || qty < 1) qty = 1;
      if (qty > 10) qty = 10;
    }
    var total = price * qty;
    var text;
    if (total >= first) {
      text = qty > 1
        ? 'These ' + qty + ' already take <b>' + pct + '% off</b> the order.'
        : 'This one already takes <b>' + pct + '% off</b> the order.';
    } else if (qty === 1 && price * 2 >= first) {
      text = 'Two packs takes <b>' + pct + '% off</b> the whole order.';
    } else {
      text = '<b>' + money(first - total) + ' more</b> takes ' + pct + '% off the whole order.';
    }
    line.innerHTML = text + ' Mix any packs you like.';
    go.setAttribute('href', url + '?add=' + encodeURIComponent(handle) + ':' + qty);
  };

  if (field) {
    field.addEventListener('change', paint);
    field.addEventListener('input', paint);
  }
  paint();
}

if (!TB_BOOTED) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tbPackCta);
  } else {
    tbPackCta();
  }
}

// ---------------------------------------------------------------------------
// "How often" on a product page.
//
// The hard part is that one screen produces TWO cart lines: what they want
// today, and what they want to keep coming. A Shopify line either repeats or
// it does not, so "two packs today, one every six months" is 1 one-time line
// plus 1 subscription line. The maths is (today − repeating) and (repeating).
//
// Nothing here runs unless the block rendered, and the block only renders when
// the product actually has subscription plans attached.
// ---------------------------------------------------------------------------
// Shared with the cart, which has no tiles and no tags to read — only the
// title Shopify gives it.
function tbShortName(full) {
  const guesses = [
    [/boxer|brief/i, 'Boxer Briefs'],
    [/sock/i, 'Socks'],
    [/tank/i, 'Tanks'],
    [/tee|t-shirt|crew/i, 'Tees'],
  ];
  for (let i = 0; i < guesses.length; i += 1) {
    if (guesses[i][0].test(full)) return 'TRAILBLAZER ' + guesses[i][1];
  }
  return full;
}

// ---------------------------------------------------------------------------
// The Shopify bag, made to read like the pack.
//
// Almost nobody should end up here — the pack checks out on its own page. But
// the bag icon is in the header on every page, and somebody who taps it after
// building a pack should recognise what they are looking at rather than meet a
// different shop.
//
// It is a record, not a builder: the per-item subscription switches cannot
// exist here, because those choices are already made and committed. What it
// can do is use the same names, the same spacing and the same tag.
function tbCartNames(root) {
  (root || document).querySelectorAll('.cart-item__name').forEach(function (el) {
    if (el.dataset.tbShort === '1') return;
    var full = el.textContent.trim();
    var short = tbShortName(full);
    el.dataset.tbShort = '1';
    if (short === full) return;
    // The full name stays reachable — it is still the product's real title.
    if (!el.getAttribute('title')) el.setAttribute('title', full);
    el.textContent = short;
  });
}

// An empty bag while a pack is half built is the actual fall-safe case. The
// pack is not in Shopify's cart until Check out is pressed, so somebody who
// taps the bag icon mid-build gets "Your cart is empty" — which is true, and
// reads as though their work is gone.
function tbCartPackNote() {
  const empty = document.querySelector('.cart__empty-text');
  if (!empty || empty.dataset.tbPack === '1') return;

  let pack = null;
  try {
    pack = TB_STORE.read('tb-pack-v1');
  } catch (e) {
    pack = null;
  }
  if (!pack || !Array.isArray(pack.lines) || !pack.lines.length) return;
  // A week-old pack is a stale price, the same rule the builder itself uses.
  if (!pack.at || Date.now() - pack.at > 7 * 24 * 60 * 60 * 1000) return;

  empty.dataset.tbPack = '1';
  const units = Number(pack.units) || pack.lines.length;
  const url = typeof pack.url === 'string' && pack.url.charAt(0) === '/'
    ? pack.url
    : '/';

  const note = document.createElement('div');
  note.className = 'tb-cartpack';
  note.innerHTML =
    '<p class="tb-cartpack__lead"><strong>' + units + ' pack'
    + (units === 1 ? '' : 's') + '</strong> waiting in your pack builder.</p>'
    + '<p class="tb-cartpack__sub">Nothing is lost — a pack moves into the bag'
    + ' when you check out from it.</p>'
    + '<a class="tb-cartpack__go" href="' + url + '">Back to my pack</a>';
  empty.insertAdjacentElement('afterend', note);
}

function tbCartWatch() {
  tbCartNames(document);
  tbCartPackNote();
  // Dawn re-renders the cart from the Section Rendering API on every quantity
  // change, which replaces these nodes wholesale. Watching is the only way to
  // keep up without hooking every one of its call sites.
  var spots = document.querySelectorAll('cart-drawer, .cart__items, cart-items, .cart__warnings');
  if (!spots.length || typeof MutationObserver !== 'function') return;
  var pending = null;
  var watcher = new MutationObserver(function () {
    clearTimeout(pending);
    pending = setTimeout(function () {
      tbCartNames(document);
      tbCartPackNote();
    }, 60);
  });
  spots.forEach(function (spot) {
    watcher.observe(spot, { childList: true, subtree: true });
  });
}

if (!TB_BOOTED) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tbCartWatch);
  } else {
    tbCartWatch();
  }
}

function tbResupply() {
  var box = document.querySelector('[data-tb-resup]');
  if (!box) return;

  var form = document.getElementById(box.dataset.form);
  if (!form) return;

  var priceEl = box.querySelector('[data-tb-resup-price]');
  var qtyEl = box.querySelector('[data-tb-resup-qty]');
  var planEl = box.querySelector('[data-tb-resup-plan]');
  var stepper = box.querySelector('[data-tb-resup-stepper]');
  var modes = box.querySelectorAll('[data-tb-resup-mode]');

  var prices = {};
  try {
    prices = JSON.parse(box.querySelector('[data-tb-resup-data]').textContent);
  } catch (e) {
    return;
  }

  var repeatQty = 1;

  var money = function (cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  var currentVariant = function () {
    var field = form.querySelector('[name="id"]');
    return field ? String(field.value) : '';
  };

  // Dawn keeps the quantity input OUTSIDE the form and ties the two together
  // with a form="" attribute, so form.querySelector never finds it. It is in
  // form.elements, which is the list of controls the form will actually submit
  // — association, not nesting. Looking in the wrong place made today's
  // quantity read as 1 no matter what the pack cards said, so a 2-pack order
  // with a subscription lost the one-off half of it silently.
  var qtyField = function () {
    var el = form.elements ? form.elements.quantity : null;
    if (el && !el.tagName && el.length) el = el[0]; // more than one so named
    return el || document.querySelector('[name="quantity"][form="' + form.id + '"]');
  };

  var todayQty = function () {
    var field = qtyField();
    var n = field ? parseInt(field.value, 10) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  // The select lists the group's plans; a variant only carries the ones it was
  // actually given an allocation for. Sending one it does not have gets the
  // whole add rejected, so fall back to a plan the variant really offers.
  var chosenPlan = function () {
    var row = prices[currentVariant()];
    var offered = row && row.plans ? Object.keys(row.plans) : [];
    var want = planEl ? String(planEl.value) : '';
    if (want && offered.indexOf(want) !== -1) return want;
    return offered[0] || want;
  };

  var isRepeat = function () {
    var on = box.querySelector('[data-tb-resup-mode]:checked');
    return !!on && on.value === 'repeat';
  };

  var setTodayQty = function (n) {
    var field = qtyField();
    if (!field || Number(field.value) === n) return;
    // Prefer the pack card, so the cards never show "One pack" while the
    // quantity underneath them says three. Ticking it sets the field itself.
    var tier = document.querySelector('.tb-tiers input[type="radio"][value="' + n + '"]');
    if (tier && !tier.checked) {
      tier.checked = true;
      tier.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    field.value = n;
    field.dispatchEvent(new Event('change', { bubbles: true }));
  };

  var paint = function () {
    // A subscription line's first delivery IS today's order, so the two
    // quantities cannot be independent. The dependency runs this way round:
    // raising the resupply raises what ships today, rather than the resupply
    // being blocked by a number the shopper has not reached yet.
    if (repeatQty < 1) repeatQty = 1;
    if (repeatQty > 10) repeatQty = 10;
    if (isRepeat() && todayQty() < repeatQty) setTodayQty(repeatQty);
    qtyEl.textContent = repeatQty;

    var row = prices[currentVariant()];
    var planId = chosenPlan();
    var wasEl = box.querySelector('[data-tb-resup-was]');
    // Liquid already printed a correct price for the first variant, so a miss
    // here leaves that standing rather than blanking it. Both numbers are for
    // the whole delivery, not one pack — the line underneath says "2 packs",
    // so a price for one pack beside it would simply be the wrong number.
    if (row && row.plans && row.plans[planId] != null) {
      priceEl.textContent = money(row.plans[planId] * repeatQty);
      if (wasEl) wasEl.textContent = money(row.base * repeatQty);
    }

    var what = box.querySelector('[data-tb-resup-what]');
    if (what && row && row.name) {
      var every = planEl && planEl.selectedOptions[0]
        ? planEl.selectedOptions[0].textContent.trim().toLowerCase()
        : 'every time';
      // When today's order is bigger than the repeat, say so in full. Two
      // lines go into the cart and the shopper should know that before the
      // cart tells them.
      var nowQty = Math.max(todayQty(), repeatQty);
      var lead = nowQty > repeatQty
        ? nowQty + (nowQty === 1 ? ' pack now, then ' : ' packs now, then ')
        : '';
      what.textContent = lead + repeatQty + (repeatQty === 1 ? ' pack of ' : ' packs of ')
        + row.name + ', ' + every + '. Skip or cancel any time.';
    }

    var repeating = isRepeat();
    box.classList.toggle('tb-resup--on', repeating);
    if (stepper) stepper.setAttribute('aria-disabled', repeating ? 'false' : 'true');
  };

  // Picking a frequency, or a number, IS choosing to subscribe — the same way
  // the stepper already worked. Greying the controls out until the radio is
  // ticked made the option look switched off at the very moment it is trying
  // to be noticed, and left the shopper a step to discover before the controls
  // would answer. Nothing is submitted as recurring unless this radio is on,
  // and it is visibly on once either control is touched.
  var choose = function () {
    var on = box.querySelector('[data-tb-resup-mode][value="repeat"]');
    if (on && !on.checked) on.checked = true;
  };

  // Outside the form, so a change there never bubbled to the form listener
  // below — the pack cards could move the quantity without this block noticing.
  var qf = qtyField();
  if (qf) qf.addEventListener('change', paint);

  modes.forEach(function (el) { el.addEventListener('change', paint); });
  if (planEl) planEl.addEventListener('change', function () { choose(); paint(); });
  form.addEventListener('change', paint);

  if (stepper) {
    stepper.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-tb-resup-step]');
      if (!btn) return;
      event.preventDefault();
      repeatQty += Number(btn.dataset.tbResupStep);
      choose();
      paint();
    });
  }

  // Captured on the document so this runs before the theme's own submit
  // handler and can take the add-to-cart over entirely when repeating.
  document.addEventListener('submit', function (event) {
    if (event.target !== form || !isRepeat()) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    var variant = currentVariant();
    if (!variant) return;
    var repeat = repeatQty;
    var today = Math.max(todayQty(), repeat);
    var once = today - repeat;

    var items = [];
    if (once > 0) items.push({ id: variant, quantity: once });
    var plan = chosenPlan();
    items.push({ id: variant, quantity: repeat, selling_plan: plan ? Number(plan) : undefined });

    var button = form.querySelector('[name="add"]');
    var label = button ? button.innerHTML : '';
    if (button) { button.setAttribute('aria-disabled', 'true'); button.classList.add('loading'); }

    tbTrack('resupply_added_to_cart', {
      variant_id: variant,
      today: today,
      repeating: repeat,
      selling_plan: plan || '',
    });

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: items,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && data.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(data);
          if (drawer.open) drawer.open();
        } else {
          window.location.href = window.Shopify.routes.root + 'cart';
        }
      })
      .catch(function () {
        window.location.href = window.Shopify.routes.root + 'cart';
      })
      .finally(function () {
        if (button) {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          button.innerHTML = label;
        }
      });
  }, true);

  paint();
}

if (!TB_BOOTED) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tbResupply);
  } else {
    tbResupply();
  }
}

// ---------------------------------------------------------------------------
// Size guide.
//
// Delegated from the document so it works for the product page AND for the
// quick-view drawer, whose markup arrives after this script has already run.
//
// Uses a native <dialog>, which brings focus trapping, Escape to close and the
// top layer for free — inside the pack drawer that last part matters, because a
// hand-rolled overlay would be trapped under the drawer it was opened from.
// ---------------------------------------------------------------------------
function tbSizeGuide() {
  document.addEventListener('click', function (event) {
    var open = event.target.closest('[data-tb-sg-open]');
    if (open) {
      var dlg = document.getElementById(open.dataset.tbSgOpen);
      if (!dlg) return;
      event.preventDefault();
      tbTrack('size_guide_opened', { id: open.dataset.tbSgOpen });
      if (typeof dlg.showModal === 'function') {
        dlg.showModal();
      } else {
        dlg.setAttribute('open', '');
      }
      return;
    }

    var close = event.target.closest('[data-tb-sg-close]');
    if (close) {
      var owner = close.closest('dialog');
      if (owner) owner.close ? owner.close() : owner.removeAttribute('open');
      return;
    }

    // Clicking the backdrop. The panel fills the dialog, so a click that lands
    // on the dialog itself landed outside the panel.
    if (event.target.matches && event.target.matches('dialog.tb-sg')) {
      event.target.close ? event.target.close() : event.target.removeAttribute('open');
    }
  });
}

if (!TB_BOOTED) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tbSizeGuide);
  } else {
    tbSizeGuide();
  }
}

// ---------------------------------------------------------------------------
// The bag count, kept honest.
//
// Shopify renders the header's count bubble when the page is built. That is
// fine until the bag changes without a page build — which is exactly what the
// builder does, and exactly what Safari's back-forward cache reproduces when
// somebody backs out of checkout: the restored page is the one from before,
// showing an empty bag over a bag that is not empty.
//
// A shopper with no badge has no reason to believe anything is in there, and
// no way to find the one screen where it can be fixed. So the count is
// re-read on load and again whenever a cached page is restored.
function tbPaintBag(count) {
  var host = document.getElementById('cart-icon-bubble')
    || document.querySelector('.header__icon--cart');
  if (!host) return;
  var bubble = host.querySelector('.cart-count-bubble');
  if (count > 0) {
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      host.appendChild(bubble);
    }
    bubble.innerHTML = '<span aria-hidden="true">' + (count < 100 ? count : '') + '</span>'
      + '<span class="visually-hidden">' + count + ' items</span>';
  } else if (bubble) {
    bubble.remove();
  }
}

function tbSyncBag() {
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
    .then(function (res) { return res.json(); })
    .then(function (cart) { tbPaintBag(cart.item_count || 0); })
    .catch(function () { /* leave whatever the page rendered */ });
}

if (!TB_BOOTED) {
  // persisted = restored from the back-forward cache, so nothing re-rendered.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) tbSyncBag();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tbSyncBag);
  } else {
    tbSyncBag();
  }
}
